import { type FC, useContext } from 'react';
import { createPortal } from 'react-dom';
import { FlyingImg } from '@components/DiceGame/DiceAnimation/FlyingDice.tsx';

import { HeaderAnimationContext } from '@/context/HeaderAnimationContext';
import { setActiveBoard } from '@/redux/reducers/diceGameReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { DiceGameContext } from '../context';
import { DiceAnimation } from '../DiceAnimation/DiceAnimation';
import { DiceCount } from '../DiceCount/DiceCount';
import { useDiceBoardSize } from '../hooks/dice-board-size';
import { useMainDiceGame } from '../hooks/main-dice-game';
import { useMiniDiceGame } from '../hooks/mini-dice-game';
import { RollBalance } from '../MainGameBalance/RollBalance';
import { RewardAnimation } from '../Reward/RewardAnimation';
import RollPower from '../RollPower/RollPower';
import { ActiveTile } from '../Tiles/Tile/ActiveTile';
import { TilesBoard } from '../TilesBoard/TilesBoard';

import css from './styles.module.scss';

const DiceBoard: FC = () => {
  const { diceBoardRef, oneCellSize } = useDiceBoardSize();
  const activeBoard = useAppSelector((state) => state.diceGame.activeBoard);
  const dispatch = useAppDispatch();
  const {
    activeTileRef,
    diceFromRef,
    diceToRef,
    energyFromRef,
    energyToRef,
    flyingAnimation,
    setFlyingAnimation,
    cardsRewardFromRef,
    isRewardScaled,
    setIsRewardScaled,
  } = useContext(DiceGameContext);
  const tileAnimationSteps = useAppSelector(
    (state) => state.diceGame.tileAnimationSteps,
  );
  const { tgemBalanceRef, setTgemBalanceScale } = useContext(
    HeaderAnimationContext,
  );

  const mainGame = useMainDiceGame();

  const miniGame = useMiniDiceGame({
    activeTileRef,
    tileAnimationSteps,
  });

  const boardPosition =
    activeBoard === 'miniGame'
      ? miniGame.boardPosition
      : mainGame.boardPosition;

  const diceCount =
    activeBoard === 'dice' ? mainGame.diceCount : miniGame.diceCount;

  const diceCountVariant =
    activeBoard === 'dice'
      ? 'default'
      : miniGame.boardKind === 'str'
        ? 'stars-mini-game'
        : 'tgem-mini-game';

  if (boardPosition === undefined) {
    return null;
  }

  const activeTileInitial = tileAnimationSteps
    ? tileAnimationSteps[boardPosition % 20]
    : null;

  const energyCount = mainGame.energyCount ?? 0;

  return (
    <div className="w-[50%] flex flex-col items-center h-full">
      <div className="w-full flex items-center justify-center flex-1 max-h-[112px]">
        {activeBoard === 'dice' ? (
          <RollBalance />
        ) : (
          // <RollEnergy
          //   energy={mainGame.energyCount}
          //   className="mt-[min(2.6svh,_1.5rem)] mb-[min(5.4svh,_3.125rem)]"
          // />
          <RewardAnimation
            className="mt-[min(3.45svh,_2rem)] mb-[min(4.53svh,_2.625rem)] w-[5.9375rem] justify-start"
            kind={miniGame.boardKind}
            value={miniGame.reward}
            scale={isRewardScaled ? 1.4 : 1}
          />
        )}
      </div>
      <div ref={diceBoardRef} className={css.diceBoard}>
        <TilesBoard boardPosition={boardPosition} />
        <DiceAnimation style={{ gridArea: 'dice' }} result={4} />
        {!!activeTileInitial && (
          <ActiveTile
            ref={activeTileRef}
            size={oneCellSize}
            initial={{
              x: activeTileInitial[0],
              y: activeTileInitial[1],
            }}
          />
        )}
        {activeBoard === 'miniGame' && miniGame.diceCount !== undefined && (
          <DiceCount
            variant={diceCountVariant!}
            quantity={miniGame.diceCount}
          />
        )}
      </div>
      <div className="flex flex-col px-auto gap-4 px-10 self-stretch pt-1 mt-auto">
        {activeBoard === 'dice' && (
          <RollPower diceCount={diceCount} energyCount={energyCount} />
        )}
        {/* <div className="flex items-center justify-between gap-4">
          {mainGame.diceCount !== undefined && activeBoard === 'dice' && (
            <DiceCount
              variant={diceCountVariant!}
              quantity={mainGame.diceCount}
              withAddButton={activeBoard === 'dice'}
              scale={flyingAnimation === 'dice' ? 1.3 : 1}
            />
          )}
          {diceCount !== undefined && (
            <div className={'flex flex-1'}>
              {activeBoard === 'dice' &&
              (diceCount === 0 || energyCount === 0) ? (
                <BuyRollEnergyButton
                  kind={diceCount === 0 ? 'dice' : 'energy'}
                />
              ) : (
                <RollButton
                  scope={scope}
                  animate={animate}
                  activeTileControll={activeTileRef.current}
                  rollRequest={rollRequest}
                  responseHandler={responseHandler}
                  boardPosition={boardPosition}
                  isLoading={
                    rollMainGameRequestState.isLoading ||
                    rollMiniGameRequestState.isLoading
                  }
                  inactive={
                    !diceCount || (activeBoard === 'dice' && !energyCount)
                  }
                  disabled={
                    !diceCount ||
                    (activeBoard === 'dice' && !energyCount) ||
                    rollMainGameRequestState.isLoading ||
                    rollMiniGameRequestState.isLoading
                  }
                />
              )}
            </div>
          )}
        </div> */}
      </div>
      <>
        {flyingAnimation === 'dice' &&
          Array.from({ length: 2 }).map((_, index) => (
            <FlyingImg
              duration={0.8}
              key={index + 'dice'}
              isFlying
              fromRef={diceFromRef}
              toRef={diceToRef}
              delay={index * 0.2}
              startScale={1.4}
              scale={1.6}
              animationType="arc"
              completeCallback={() => {
                if (index === 0) return;
                setFlyingAnimation(null);
              }}
              type="dice"
            />
          ))}
        {flyingAnimation === 'energy' &&
          Array.from({ length: 2 }).map((_, index) => (
            <FlyingImg
              duration={0.8}
              key={index + 'energy'}
              isFlying
              fromRef={energyFromRef}
              toRef={energyToRef}
              animationType="arc"
              completeCallback={() => {
                if (index === 0) return;
                setFlyingAnimation(null);
              }}
              delay={index * 0.2}
              type="energy"
            />
          ))}
        {createPortal(
          flyingAnimation === 'tgem' &&
            Array.from({ length: 8 }).map((_, index) => (
              <FlyingImg
                toRef={tgemBalanceRef}
                duration={0.5}
                key={index + '_tgem'}
                isFlying
                fromRef={cardsRewardFromRef}
                animationType="arc"
                completeCallback={() => {
                  if (index === 7) {
                    setFlyingAnimation(null);
                    setIsRewardScaled(false);
                    setTgemBalanceScale(false);
                    dispatch(setActiveBoard('dice'));
                  }
                }}
                delay={index * 0.2}
                type="tgem"
                scale={1.6}
              />
            )),
          document.getElementById('root')!,
        )}
      </>
    </div>
  );
};

export { DiceBoard };
